import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LogOut,
  ChevronRight,
  Navigation,
  MapPin,
  Phone,
  Calendar,
} from "lucide-react";
import { PlanningCalendar } from "./PlanningCalendar";
import { DossierNotes } from "./DossierNotes";
import { SignedQuoteUpload } from "./commercial/SignedQuoteUpload";
import type { Job, User } from "../types";

interface CommercialAppProps {
  jobs: Job[];
  currentUser: User;
  onUpdateJob: (job: Job) => void | Promise<{ error: string | null } | void>;
  onLogout: () => void;
  ownerCompanyName?: string;
  ownerLogoUrl?: string | null;
}

function formatJobDate(dateStr: string): string {
  const [y, m, d] = dateStr.split("-");
  if (!y || !m || !d) return dateStr;
  return `${d}/${m}/${y}`;
}

function navigationLinks(address: string) {
  const q = encodeURIComponent(address.trim());
  return {
    google: `https://www.google.com/maps/search/?api=1&query=${q}`,
    waze: `https://waze.com/ul?q=${q}&navigate=yes`,
  };
}

export function CommercialApp({
  jobs,
  currentUser,
  onUpdateJob,
  onLogout,
  ownerCompanyName = "",
  ownerLogoUrl,
}: CommercialAppProps) {
  const { t } = useTranslation();
  const [detailJob, setDetailJob] = useState<Job | null>(null);
  const [savingQuote, setSavingQuote] = useState(false);

  const scheduledJobs = useMemo(
    () =>
      jobs
        .filter((j) => j.date && j.status !== "cancelled")
        .sort((a, b) => {
          const d = (a.date || "").localeCompare(b.date || "");
          if (d !== 0) return d;
          return (a.time || "").localeCompare(b.time || "");
        }),
    [jobs]
  );

  const handleQuoteUploaded = async (nextPaths: string[]) => {
    if (!detailJob) return;
    setSavingQuote(true);
    try {
      const urls = nextPaths.length ? nextPaths : undefined;
      await onUpdateJob({ ...detailJob, signedQuoteUrls: urls });
      setDetailJob((prev) => (prev ? { ...prev, signedQuoteUrls: urls } : null));
    } finally {
      setSavingQuote(false);
    }
  };

  const brandTitle = ownerCompanyName || t("commercial.appTitle");
  const links = detailJob ? navigationLinks(detailJob.clientAddress) : null;

  return (
    <div className="min-h-screen flex flex-col bg-slate-100 dark:bg-slate-900">
      <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-gray-200/80 dark:border-white/10 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          {ownerLogoUrl ? (
            <img src={ownerLogoUrl} alt="" className="h-9 w-9 rounded-xl object-cover shrink-0" />
          ) : (
            <div className="h-9 w-9 rounded-xl bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center shrink-0">
              <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{brandTitle}</p>
            <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
              {currentUser.name} · {t("commercial.planningOnly")}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="flex items-center gap-1.5 rounded-xl border border-gray-200 dark:border-white/10 px-3 py-2 text-sm font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-white/5 shrink-0"
        >
          <LogOut size={18} />
          <span className="hidden sm:inline">{t("commercial.logout")}</span>
        </button>
      </header>

      {!detailJob ? (
        <main className="flex-1 px-3 py-4 max-w-3xl mx-auto w-full">
          {scheduledJobs.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 dark:border-white/15 bg-white dark:bg-slate-800/50 p-10 text-center">
              <Calendar className="mx-auto h-10 w-10 text-gray-400 dark:text-slate-500 mb-3" />
              <p className="font-medium text-gray-800 dark:text-slate-200">{t("commercial.noAppointments")}</p>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">{t("commercial.noAppointmentsHint")}</p>
            </div>
          ) : (
            <PlanningCalendar jobs={scheduledJobs} onJobSelect={setDetailJob} readOnly />
          )}
        </main>
      ) : (
        <main className="flex-1 px-4 py-3 max-w-lg mx-auto w-full">
          <button
            type="button"
            onClick={() => setDetailJob(null)}
            className="mb-4 flex items-center gap-2 text-gray-500 dark:text-gray-400 font-medium"
          >
            <ChevronRight className="rotate-180" size={20} />
            {t("commercial.backToPlanning")}
          </button>

          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-white/10 rounded-2xl shadow-xl overflow-hidden mb-6">
            <div className="bg-blue-600 dark:bg-blue-700 p-6 text-white">
              <p className="text-xs uppercase tracking-wider opacity-80 mb-2">{t("commercial.appointment")}</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs opacity-80">{t("commercial.date")}</p>
                  <p className="text-lg font-bold">{detailJob.date ? formatJobDate(detailJob.date) : "—"}</p>
                </div>
                <div>
                  <p className="text-xs opacity-80">{t("commercial.time")}</p>
                  <p className="text-lg font-bold">{detailJob.time || "—"}</p>
                </div>
              </div>
              <p className="text-lg font-medium mt-3 opacity-95">{detailJob.clientName}</p>
            </div>

            <div className="p-6 space-y-6">
              {links && (
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href={links.google}
                    target="_blank"
                    rel="noreferrer"
                    className="flex flex-col items-center justify-center bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 p-4 rounded-2xl active:scale-95 transition-transform"
                  >
                    <Navigation size={28} className="mb-2" />
                    <span className="font-bold text-sm text-center">{t("commercial.openMaps")}</span>
                  </a>
                  <a
                    href={links.waze}
                    target="_blank"
                    rel="noreferrer"
                    className="flex flex-col items-center justify-center bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 p-4 rounded-2xl active:scale-95 transition-transform"
                  >
                    <Navigation size={28} className="mb-2" />
                    <span className="font-bold text-sm text-center">Waze</span>
                  </a>
                </div>
              )}

              <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl">
                <MapPin className="text-gray-400 shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-xs text-gray-400 uppercase font-bold mb-1">{t("commercial.address")}</p>
                  <p className="text-gray-900 dark:text-gray-100 font-medium leading-relaxed">
                    {detailJob.clientAddress}
                  </p>
                </div>
              </div>

              {detailJob.clientPhone && (
                <a
                  href={`tel:${detailJob.clientPhone}`}
                  className="flex items-center gap-3 p-4 rounded-2xl border border-green-200 dark:border-green-500/30 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300"
                >
                  <Phone size={22} />
                  <span className="font-semibold">{detailJob.clientPhone}</span>
                </a>
              )}

              {detailJob.notes?.trim() ? (
                <div className="rounded-2xl border border-amber-200/80 dark:border-amber-500/20 bg-amber-50/80 dark:bg-amber-500/10 p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-800/80 dark:text-amber-300/90 mb-2">
                    {t("commercial.partnerRemarks")}
                  </p>
                  <p className="text-sm leading-relaxed text-gray-800 dark:text-slate-200 whitespace-pre-wrap">
                    {detailJob.notes.trim()}
                  </p>
                </div>
              ) : null}

              <DossierNotes dossierId={detailJob.id} currentUser={currentUser} />

              <SignedQuoteUpload
                jobId={detailJob.id}
                existingPaths={detailJob.signedQuoteUrls}
                required
                onChange={handleQuoteUploaded}
                disabled={savingQuote}
              />
            </div>
          </div>
        </main>
      )}
    </div>
  );
}
